import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useGraphQLAuth } from 'hooks/useGraphQLAuth';
import { useToast } from 'hooks/useToast';

interface CheckBoxChangeEvent {
  checked: boolean;
  value: any;
}

const validationSchema = Yup.object({
  question: Yup.string()
    .required('Pergunta é obrigatória')
    .min(5, 'Pergunta deve ter pelo menos 5 caracteres'),
  answer: Yup.string()
    .required('Resposta é obrigatória')
    .min(10, 'Resposta deve ter pelo menos 10 caracteres'),
  displayOrder: Yup.number()
    .required('Ordem é obrigatória')
    .min(1, 'Ordem deve ser maior que 0')
    .integer('Ordem deve ser um número inteiro'),
  isActive: Yup.boolean()
    .required('Status é obrigatório'),
});

const initInputsValues = {
  question: '',
  answer: '',
  displayOrder: 1,
  isActive: true,
};

export const useFaqForm = (onSuccess?: () => void, editingFaq?: any, providedEventId?: string) => {
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  
  const [initialInputsValues, setInitialInputsValues] = useState(initInputsValues);
  const [isLoading, setIsLoading] = useState(false);
  
  const isEditing = !!editingFaq;

  // Initialize form values based on editing mode
  useEffect(() => {
    if (editingFaq) {
      const editValues = {
        question: editingFaq.question || '',
        answer: editingFaq.answer || '',
        displayOrder: editingFaq.displayOrder || 1,
        isActive: editingFaq.isActive !== undefined ? editingFaq.isActive : true,
      };
      setInitialInputsValues(editValues);
      formik.setValues(editValues);
    } else {
      setInitialInputsValues(initInputsValues);
      formik.setValues(initInputsValues);
    }
  }, [editingFaq]);

  const sendData = async (values: any) => {
    setIsLoading(true);

    try {
      const input = {
        question: values.question.trim(),
        answer: values.answer.trim(),
        displayOrder: parseInt(values.displayOrder.toString()),
        isActive: values.isActive,
      };

      if (isEditing) {
        // Update existing FAQ
        await connectGraphQLApi({
          query: `
            mutation BackofficeUpdateEventFaq($eventFaqId: ID!, $input: EventFaqUpdateInput!) {
              backofficeUpdateEventFaq(eventFaqId: $eventFaqId, input: $input) {
                eventFaqId
                question
                answer
                displayOrder
                isActive
              }
            }
          `,
          variables: {
            eventFaqId: editingFaq.eventFaqId,
            input
          },
          errorMessage: 'Falha ao atualizar FAQ',
          useNewApi: true,
        });

        setToast({
          type: 'success',
          message: 'FAQ atualizada com sucesso!',
          isActive: true,
          timeToRemoveToast: 3000,
        });
      } else {
        // Create new FAQ - include eventId for creation
        const createInput = {
          ...input,
          eventId: providedEventId,
        };

        await connectGraphQLApi({
          query: `
            mutation BackofficeCreateEventFaq($input: EventFaqInput!) {
              backofficeCreateEventFaq(input: $input) {
                eventFaqId
                question
                answer
                displayOrder
                isActive
              }
            }
          `,
          variables: { input: createInput },
          errorMessage: 'Falha ao criar FAQ',
          useNewApi: true,
        });

        setToast({
          type: 'success',
          message: 'FAQ criada com sucesso!',
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
      setToast({
        type: 'error',
        message: `Erro ao ${isEditing ? 'atualizar' : 'criar'} FAQ. Tente novamente.`,
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: initialInputsValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: sendData,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.handleChange(e);
  };

  const handleCheckboxChange = (event: CheckBoxChangeEvent) => {
    formik.setFieldValue('isActive', event.checked);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  return {
    handleInputChange,
    handleCheckboxChange,
    inputsValues: formik.values,
    touched: formik.touched,
    errors: formik.errors,
    handleSubmit,
    isLoading,
    isEditing,
  };
}; 